import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Users, Home, ArrowRight, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { ReconciliationIssueRecord, GrowerRecord } from '@/types/database';
import { reconciliationService, GrowerImpactResult } from '@/services/reconciliationService';
import { supabase } from '@/lib/supabaseClient';

interface GrowerResolutionModalProps {
  issue: ReconciliationIssueRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GrowerResolutionModal: React.FC<GrowerResolutionModalProps> = ({
  issue,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [decision, setDecision] = useState<'production_unit' | 'co_grower' | 'alias' | 'custom'>('production_unit');
  const [growers, setGrowers] = useState<GrowerRecord[]>([]);
  const [baseGrowerId, setBaseGrowerId] = useState<string>('');
  const [unitName, setUnitName] = useState<string>('');
  const [newGrowerName, setNewGrowerName] = useState<string>('');
  const [aliasGrowerId, setAliasGrowerId] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [impact, setImpact] = useState<GrowerImpactResult | null>(null);
  const [loadingImpact, setLoadingImpact] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Parse legacy string for suggestions
  useEffect(() => {
    if (!issue || !isOpen) return;

    setError(null);
    const raw = issue.legacy_value || '';
    
    // Default guesses: e.g. "Anonuevo, Benjie - Eva" -> Base: "Anonuevo, Benjie", Unit: "Eva"
    if (raw.includes(' - ')) {
      const parts = raw.split(' - ');
      const basePart = parts[0]?.trim() || '';
      const unitPart = parts[1]?.trim() || '';
      setUnitName(unitPart);
      setNewGrowerName(raw);
    } else if (raw.includes('-')) {
      const parts = raw.split('-');
      const basePart = parts[0]?.trim() || '';
      const unitPart = parts[1]?.trim() || '';
      setUnitName(unitPart);
      setNewGrowerName(raw);
    } else {
      setUnitName('');
      setNewGrowerName(raw);
    }

    // Fetch verified master growers
    supabase
      .from('growers')
      .select('id, full_name, grower_code, is_active')
      .eq('is_void', false)
      .order('full_name', { ascending: true })
      .then(({ data }) => {
        const list = (data as any) || [];
        setGrowers(list);

        // Auto-match base grower if possible
        if (raw.includes(' - ') || raw.includes('-')) {
          const baseName = (raw.includes(' - ') ? raw.split(' - ')[0] : raw.split('-')[0]).trim();
          const matched = list.find((g: any) => g.full_name.toLowerCase() === baseName.toLowerCase());
          if (matched) {
            setBaseGrowerId(matched.id);
          }
        }
      });

    // Fetch impact preview
    setLoadingImpact(true);
    reconciliationService.getGrowerDisambiguationImpact(raw).then((res) => {
      setImpact(res);
      setLoadingImpact(false);
    });
  }, [issue, isOpen]);

  const handleSubmit = async () => {
    if (!issue) return;
    setError(null);
    setSubmitting(true);

    try {
      const affectedCycleIds = impact?.cycles.map((c) => c.id) || [];
      const baseGrower = growers.find((g) => g.id === baseGrowerId);

      await reconciliationService.resolveCompoundGrower(
        issue.id,
        {
          decision,
          base_grower_id: baseGrowerId || undefined,
          base_grower_name: baseGrower?.full_name,
          unit_name: unitName || undefined,
          new_grower_name: newGrowerName || undefined,
          alias_grower_id: aliasGrowerId || undefined,
          affected_cycle_ids: affectedCycleIds,
          notes: adminNotes,
        },
        adminNotes
      );

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Failed to resolve compound grower issue:', err);
      setError(err.message || 'Failed to save resolution');
    } finally {
      setSubmitting(false);
    }
  };

  if (!issue) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">
                Grower / Pen Disambiguation
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Resolve compound names and normalize relationships while preserving original Excel evidence.
              </p>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Ground Truth Card */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Excel Ground Truth</span>
            <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-200 text-slate-700 font-bold">
              {issue.source_sheet}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <span className="text-slate-500">Original Value:</span>
              <div className="font-mono font-bold text-slate-900 text-sm mt-0.5">{issue.legacy_value}</div>
            </div>
            <div>
              <span className="text-slate-500">Flagged Reason:</span>
              <div className="text-slate-700 mt-0.5">{issue.description}</div>
            </div>
          </div>
        </div>

        {/* Interactive Decision Radio Group */}
        <div className="space-y-3">
          <Label className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Select Resolution Option:
          </Label>

          {/* Option 1: Production Unit / Pen */}
          <div
            onClick={() => setDecision('production_unit')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              decision === 'production_unit'
                ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="grower_decision"
                checked={decision === 'production_unit'}
                onChange={() => setDecision('production_unit')}
                className="mt-1 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="space-y-2 flex-1">
                <div>
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <Home className="w-3.5 h-3.5 text-indigo-600" />
                    Production Unit / Pen under Base Grower (Recommended)
                  </span>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Links this raiser string to a primary grower with a specific named pen/unit (e.g. Grower: Anonuevo, Benjie + Unit: Eva).
                  </p>
                </div>

                {decision === 'production_unit' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-indigo-100">
                    <div>
                      <Label className="text-[11px] font-semibold text-slate-700">Primary Master Grower *</Label>
                      <select
                        value={baseGrowerId}
                        onChange={(e) => setBaseGrowerId(e.target.value)}
                        className="mt-1 w-full text-xs rounded-md border border-slate-300 p-2 bg-white text-slate-900 focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">-- Select Master Grower --</option>
                        {growers.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold text-slate-700">Production Unit / Pen Name *</Label>
                      <Input
                        type="text"
                        value={unitName}
                        onChange={(e) => setUnitName(e.target.value)}
                        placeholder="e.g. Eva, Pen 2"
                        className="mt-1 text-xs h-8"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Option 2: Distinct Co-Grower */}
          <div
            onClick={() => setDecision('co_grower')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              decision === 'co_grower'
                ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="grower_decision"
                checked={decision === 'co_grower'}
                onChange={() => setDecision('co_grower')}
                className="mt-1 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="space-y-2 flex-1">
                <div>
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-slate-700" />
                    Distinct Co-Grower Individual
                  </span>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Creates a separate, independent Grower entity for this specific partner.
                  </p>
                </div>

                {decision === 'co_grower' && (
                  <div className="pt-2 border-t border-indigo-100">
                    <Label className="text-[11px] font-semibold text-slate-700">New Grower Full Name *</Label>
                    <Input
                      type="text"
                      value={newGrowerName}
                      onChange={(e) => setNewGrowerName(e.target.value)}
                      placeholder="e.g. Anonuevo, Eva"
                      className="mt-1 text-xs h-8"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Option 3: Alias for Existing Grower */}
          <div
            onClick={() => setDecision('alias')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              decision === 'alias'
                ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="grower_decision"
                checked={decision === 'alias'}
                onChange={() => setDecision('alias')}
                className="mt-1 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="space-y-2 flex-1">
                <div>
                  <span className="text-xs font-bold text-slate-900">Alias for Existing Verified Grower</span>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Maps all historical cycles directly to an existing grower without a unit distinction.
                  </p>
                </div>

                {decision === 'alias' && (
                  <div className="pt-2 border-t border-indigo-100">
                    <Label className="text-[11px] font-semibold text-slate-700">Select Target Grower *</Label>
                    <select
                      value={aliasGrowerId}
                      onChange={(e) => setAliasGrowerId(e.target.value)}
                      className="mt-1 w-full text-xs rounded-md border border-slate-300 p-2 bg-white text-slate-900 focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">-- Select Target Grower --</option>
                      {growers.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Impact Preview Section */}
        <div className="p-3.5 bg-amber-50/80 border border-amber-300/80 rounded-lg space-y-2 text-xs">
          <div className="flex items-center gap-2 font-bold text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>Impact Preview & Safety Verification</span>
          </div>

          {loadingImpact ? (
            <div className="flex items-center gap-2 text-slate-500 py-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Analyzing historical production cycles...</span>
            </div>
          ) : (
            <div className="space-y-1.5 text-slate-700 text-[11px]">
              <p>
                This resolution will update normalized relationships across{' '}
                <strong className="text-slate-900 font-mono font-bold underline">
                  {impact?.cycleCount || 0} historical production cycles
                </strong>
                .
              </p>
              <p className="text-slate-500 italic">
                Original Excel strings in <code className="text-slate-700 font-mono">production_cycles.legacy_raiser_name</code> will remain permanently immutable.
              </p>
              {impact && impact.cycles.length > 0 && (
                <div className="mt-2 pt-2 border-t border-amber-200/60 max-h-24 overflow-y-auto">
                  <span className="font-semibold text-slate-600">Affected Cycles:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {impact.cycles.slice(0, 15).map((c) => (
                      <span key={c.id} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-100 text-amber-900 font-bold">
                        {c.cycle_code} ({c.batch_code})
                      </span>
                    ))}
                    {impact.cycles.length > 15 && (
                      <span className="text-[10px] text-slate-500 font-medium self-center">
                        +{impact.cycles.length - 15} more...
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Resolution Notes */}
        <div>
          <Label className="text-xs font-semibold text-slate-700">Administrator Resolution Notes (Optional)</Label>
          <Input
            type="text"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="e.g. Confirmed with operations manager that Eva is Benjie's unit"
            className="mt-1 text-xs"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="min-h-[44px] text-xs font-semibold text-slate-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (decision === 'production_unit' && !baseGrowerId)}
            className="min-h-[44px] text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Applying Mapping...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Confirm & Apply to All {impact?.cycleCount || 0} Cycles</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
